import React, { useState } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { faCalendar, faClose, faFile, faFileInvoiceDollar } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import GetSpace from "../../../utils/space"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { OrderEntity } from "../../../Objects/Order/OrderEntity"
import { CardEmployeeRegister } from "../../../components/CardEmployeeRegister/CardEmployeeRegister"
import InputField from "../../../components/InputField/InputField"
import { useSaveOrder } from "../../../hooks/Order/useSaveOrder"
import { useConfirmation } from "../../../context/useConfirmationContext"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { OrderCanceledEntity } from "../../../Objects/Order/OrderCanceledEntity"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { useEmployerLogged } from "../../../context/useEmployeeContext"
import { INPUT_SIZE } from "../../../configuration/InputSize"
import { useTranslation } from "react-i18next"
import { CanView } from "../../../components/CanView/CanView"
import { Permissions } from "../../../Enum/PermissionsType"

interface CancelOrderPupopProbs {
  order: OrderEntity
  icon: IconProp
  showFiscalCancellationNotice?: boolean
  onSave: () => void
  onClonse: () => void
}

export const CancelOrderPupop: React.FC<CancelOrderPupopProbs> = ({
  icon,
  onSave,
  order,
  showFiscalCancellationNotice = false,
  onClonse,
}) => {
  const { t } = useTranslation(["order", "fiscalIntegration"])

  const { setNewConfirmation } = useConfirmation()

  const { handleSaveOrder } = useSaveOrder()

  const { userEmployee } = useEmployerLogged()

  const [details, setDetails] = useState(order.orderPaymentEntity.description)

  const handleSubmiteSave = () => {
    const orderToCancel = new OrderEntity({
      id: order.id,
      orderCanceledEntity: new OrderCanceledEntity({
        reason: details,
        concurrencyToken: order.concurrencyToken,
      }),
    })

    handleSaveOrder(orderToCancel, onSave, true, false)
  }

  const handleChangeDetails = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDetails(e.target.value)
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("title_agree_cancel")}
              path={t("title_path_agree_cancel")}
              icon={icon}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    icon={faClose}
                    typeButton="button"
                    onClick={onClonse}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.fluxo.order.cancelOrderPupop.button.onClonse"
                  />
                </div>
              }
            />

            <form
              onSubmit={(e) => {
                e.preventDefault()
                setNewConfirmation(t("title_agree_cancel"), t("title_path_agree_cancel"), () => handleSubmiteSave())
              }}
            >
              <div className="p-2">
                {
                  <>
                    <Animate type={AnimationTypes.fadeInTraslate}>
                      <CardEmployeeRegister
                        icon={faCalendar}
                        iconColor={THEME.colors.iconBGColor}
                        date={order.date}
                        employee={userEmployee!}
                      />
                    </Animate>

                    <GetSpace top={5} />

                    {showFiscalCancellationNotice && (
                      <Animate type={AnimationTypes.fadeInTraslate}>
                        <section className="mb-3 flex gap-3 rounded-xl border-2 border-orange-200 bg-orange-50 p-3 text-orange-950">
                          <FontAwesomeIcon icon={faFileInvoiceDollar} className="mt-0.5 text-orange-600" />

                          <div className="text-xs leading-relaxed">
                            <strong className="block text-sm">{t("fiscalIntegration:saleCancellation.title")}</strong>

                            <p className="mt-1">{t("fiscalIntegration:saleCancellation.description")}</p>

                            <p className="mt-1 font-semibold">{t("fiscalIntegration:saleCancellation.reasonHint")}</p>
                          </div>
                        </section>
                      </Animate>
                    )}

                    <InputField
                      type="text"
                      icon={faFile}
                      value={details}
                      maxLength={INPUT_SIZE.ORDER.cancelMotion}
                      placeholder={t("label_reasor_cancel_place_holder")}
                      onInvalidMessage={t("label_reasor_cancel_onInvalidMessage")}
                      initialValue={order.orderPaymentEntity.description}
                      onChange={handleChangeDetails}
                      required
                      descriptionKey="interactionDescriptions:pages.fluxo.order.cancelOrderPupop.input.reasorCancelPlaceHolder"
                    />
                  </>
                }
              </div>

              <div className={defaultStyle.divisionBar}></div>

              <CanView permission={Permissions.CanEditeOrders}>
                <div className={defaultStyle.buttonsController}>
                  <Button
                    typeButton="submit"
                    type="primary"
                    size="medium"
                    bgColor="red"
                    label={t("buttons:button_cancel_now")}
                    onClick={() => {}}
                    descriptionKey="interactionDescriptions:pages.fluxo.order.cancelOrderPupop.button.cancelNow"
                  />
                </div>
              </CanView>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
